import { Collection, HttpStateData, SingleItem } from '@/clients/http'
import { clearHttpStateObject } from '@/clients/redux/actions/http'
import { selectHttpState } from '@/clients/redux/reducers/http/httpSlice'

import { useEffect } from 'react'

import { useAppSelector } from '@/hooks'

interface UseHttpStateProps<Data> {
  correlation: string
  onSuccess?(response: UseHttpStateCallbackProps<Data>): void
  onError?(response: UseHttpStateCallbackProps<Data>): void
  clearOnSuccess?: boolean
  clearOnError?: boolean
  clearFinally?: boolean
  clearOnUnmount?: boolean
  type: 'collection' | 'individual' | 'unknown'
}

interface UseHttpStateCallbackProps<Data, Error = {}> extends HttpStateData<Data, Error> {
  exists: boolean
  clear(): void
  collection: Collection<Data> | null
  individual: SingleItem<Data> | null
}

export function useHttp<Data>(props: UseHttpStateProps<Data>): UseHttpStateCallbackProps<Data> {
  const {
    clearOnSuccess,
    clearFinally,
    clearOnError,
    clearOnUnmount,
    onError,
    onSuccess,
    correlation,
    type,
  } = props

  const clearResponse = () => {
    clearHttpStateObject(correlation)
  }

  let state: UseHttpStateCallbackProps<Data> = {
    loading: false,
    success: false,
    error: null,
    data: null,
    collection: null,
    individual: null,
    payload: null,
    exists: false,
    clear: clearResponse,
  }

  const httpResponse = useAppSelector(selectHttpState)?.[correlation]

  if (httpResponse) {
    state = {
      ...state,
      ...httpResponse,
      collection: type === 'collection' ? httpResponse?.data : null,
      individual: type === 'individual' ? httpResponse?.data : null,
      exists: true,
    }
  }

  useEffect(() => {
    if (httpResponse?.success) {
      if (clearOnSuccess || clearFinally) {
        clearResponse()
      }

      if (onSuccess) {
        onSuccess(state)
      }
    }

    if (httpResponse?.error) {
      if (clearOnError || clearFinally) {
        clearResponse()
      }

      if (onError) {
        onError(state)
      }
    }
  }, [httpResponse])

  // Separate effect: a cleanup on the [httpResponse] effect would also run on every response change, and each clear
  // writes a new state object, which changes httpResponse again and loops forever.
  useEffect(() => {
    if (!clearOnUnmount) return

    return () => {
      clearHttpStateObject(correlation)
    }
  }, [correlation, clearOnUnmount])

  return state
}
